import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Globe, Network, Brain, ShieldAlert, FileText, ArrowRight, Menu, X, Github } from 'lucide-react';
import { featuresData } from '../mock';

const iconMap = {
  bot: Bot,
  globe: Globe,
  network: Network,
  brain: Brain,
  'shield-alert': ShieldAlert,
  'file-text': FileText
};

const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="max-w-[1400px] mx-auto px-[7.6923%] h-[80px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-semibold">RakshakAI</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" className="body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }} onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'} onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}>Home</a>
            <a href="#features" className="body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }} onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'} onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}>Features</a>
            <button onClick={() => navigate('/investigator')} className="body-medium transition-colors duration-300" style={{ color: 'var(--brand-primary)' }} onMouseEnter={(e) => e.target.style.color = 'var(--brand-active)'} onMouseLeave={(e) => e.target.style.color = 'var(--brand-primary)'}>Investigator Chat</button>
            <a href="#about" className="body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }} onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'} onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}>About</a>
            <a href="#contact" className="body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }} onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'} onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}>Contact</a>
            <a href="https://github.com/BEASTSHRIRAM" target="_blank" rel="noopener noreferrer" className="transition-colors duration-300" style={{ color: 'var(--text-muted)' }} onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'} onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}>
              <Github className="w-5 h-5" />
            </a>
          </div>

          {/* Mobile Menu Button and GitHub */}
          <div className="md:hidden flex items-center gap-4">
            <a href="https://github.com/BEASTSHRIRAM" target="_blank" rel="noopener noreferrer" className="transition-colors duration-300" style={{ color: 'var(--text-muted)' }}>
              <Github className="w-5 h-5" />
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t" style={{ borderColor: 'var(--border-subtle)' }}>
            <div className="px-8 py-6 flex flex-col gap-4">
              <a href="#home" className="body-medium" style={{ color: 'var(--text-muted)' }}>Home</a>
              <a href="#features" className="body-medium" style={{ color: 'var(--text-muted)' }}>Features</a>
              <button onClick={() => navigate('/investigator')} className="body-medium text-left" style={{ color: 'var(--brand-primary)' }}>Investigator Chat</button>
              <a href="#about" className="body-medium" style={{ color: 'var(--text-muted)' }}>About</a>
              <a href="#contact" className="body-medium" style={{ color: 'var(--text-muted)' }}>Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-[100px] md:pt-[140px] pb-[60px] md:pb-[100px] px-4 md:px-[7.6923%] relative cyber-grid">
        <div className="max-w-[1400px] mx-auto">
          {/* Glow Effect */}
          <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] rounded-full opacity-20 blur-[120px]" style={{ background: 'var(--brand-primary)' }}></div>
          
          <div className="relative z-10 text-center max-w-[900px] mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:display-huge mb-4 md:mb-6 font-bold">
              RakshakAI — India's Intelligent <span className="glow-text" style={{ color: 'var(--brand-primary)' }}>Cyber Investigator</span>
            </h1>
            <p className="text-sm md:text-base lg:body-large mb-8 md:mb-12 px-2" style={{ color: 'var(--text-secondary)' }}>
              AI-powered threat intelligence, investigation, and graph analysis for law enforcement & cybersecurity teams.
            </p>
            <button onClick={() => navigate('/investigator')} className="btn-primary text-sm md:text-base">
              Start Investigation
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section id="features" className="py-[60px] md:py-[100px] px-4 md:px-[7.6923%] bg-black">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-xl md:text-2xl lg:display-large text-center mb-8 md:mb-16 font-bold">Core Capabilities</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {featuresData.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div
                  key={feature.id}
                  className="p-8 smooth-transition cursor-pointer"
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '0px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 255, 209, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-subtle)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="mb-6 w-14 h-14 flex items-center justify-center" style={{ background: 'var(--brand-hover)' }}>
                    {IconComponent && <IconComponent className="w-7 h-7" style={{ color: 'var(--brand-primary)' }} />}
                  </div>
                  <h3 className="heading-2 mb-4">{feature.title}</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section Placeholder */}
      <section id="about" className="py-[60px] md:py-[100px] px-4 md:px-[7.6923%] cyber-grid relative">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="text-xl md:text-2xl lg:display-large mb-4 md:mb-8 font-bold">How It Works</h2>
          <p className="text-sm md:body-large mb-8 md:mb-12 px-2" style={{ color: 'var(--text-secondary)' }}>
            Advanced AI-driven workflow for comprehensive cyber investigations
          </p>
          <div className="flex overflow-x-auto pb-4 md:pb-0 md:grid md:grid-cols-5 gap-4 md:gap-6 max-w-[1200px] mx-auto snap-x snap-mandatory">
            {[
              { step: 1, title: "Input Data", desc: "Enter IP/URL/Message" },
              { step: 2, title: "AI Collection", desc: "Gather intelligence" },
              { step: 3, title: "Risk Analysis", desc: "ML risk scoring" },
              { step: 4, title: "Graph Mapping", desc: "Neo4j relationships" },
              { step: 5, title: "Report", desc: "AI summary" }
            ].map((item) => (
              <div key={item.step} className="p-4 md:p-6 min-w-[140px] md:min-w-0 flex-shrink-0 snap-center" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}>
                <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 flex items-center justify-center text-lg md:text-xl font-bold" style={{ background: 'var(--brand-primary)', color: '#000' }}>
                  {item.step}
                </div>
                <h4 className="font-semibold mb-1 md:mb-2 text-sm md:text-base" style={{ fontSize: 'inherit' }}>{item.title}</h4>
                <p className="text-xs md:text-sm" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-8 md:py-12 px-4 md:px-[7.6923%] border-t" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="max-w-[1400px] mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4 md:mb-6">
            <span className="text-lg md:text-xl font-semibold">RakshakAI</span>
          </div>
          <p className="text-sm md:body-medium mb-4" style={{ color: 'var(--text-muted)' }}>
            © 2025 RakshakAI — Built for Cyber Safety
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <a href="#" className="text-sm md:body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }}>Privacy Policy</a>
            <a href="#" className="text-sm md:body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }}>Terms of Service</a>
            <a href="#contact" className="text-sm md:body-medium transition-colors duration-300" style={{ color: 'var(--text-muted)' }}>Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;